import React, { useState } from 'react';
import { AlertCircleIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { services } from '../data/services';
import { usStates } from '../data/states';
import { company } from '../data/company';
interface FormValues {
  fullName: string;
  email: string;
  phone: string;
  state: string;
  service: string;
  contactMethod: string;
  message: string;
}
type Errors = Partial<Record<keyof FormValues, string>>;
const initialValues: FormValues = {
  fullName: '',
  email: '',
  phone: '',
  state: '',
  service: '',
  contactMethod: 'Email',
  message: ''
};
const fieldClass = 'w-full border border-navy-200 bg-white px-4 py-3 text-[15px] text-navy-900 transition-colors duration-150 ease-out placeholder:text-navy-400 hover:border-navy-300 focus:border-navy-600 focus:outline-none';
const labelClass = 'mb-2 block text-[13px] font-medium text-navy-800';
const ACCESS_KEY = '13451597-a00d-4422-8b5b-f5314d13c39b';
export function ContactForm() {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'submitted'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const update = (field: keyof FormValues, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  };
  const validate = (): Errors => {
    const next: Errors = {};
    if (!values.fullName.trim()) next.fullName = 'Please enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) next.email = 'Please enter a valid email address.';
    if (!values.state) next.state = 'Please select your state.';
    if (!values.service) next.service = 'Please select a service of interest.';
    if (!values.message.trim()) next.message = 'Please tell us briefly how we can help.';
    return next;
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitError(null);
    setStatus('submitting');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: ACCESS_KEY,
          subject: `Website inquiry — ${values.service || 'General Inquiry'}`,
          from_name: `${company.name} Website`,
          name: values.fullName,
          email: values.email,
          phone: values.phone,
          state: values.state,
          service_of_interest: values.service,
          preferred_contact_method: values.contactMethod,
          message: values.message
        })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setStatus('submitted');
      } else {
        setStatus('idle');
        setSubmitError('We were unable to send your inquiry. Please try again in a moment.');
      }
    } catch {
      setStatus('idle');
      setSubmitError('We were unable to send your inquiry. Please check your connection and try again.');
    }
  };
  if (status === 'submitted') {
    return <div role="status" className="flex flex-col items-start border border-navy-100 bg-navy-50 p-8 sm:p-10" data-id="element-22" data-mp="opx5jlmba1bs">
        <CheckCircle2Icon className="h-8 w-8 text-gold-500" aria-hidden="true" data-id="element-23" data-mp="spo2k72fldor" />
        <h2 className="mt-5 font-serif text-[24px] text-navy-950" data-id="element-24" data-mp="epwltykj5j72">
          Thank you for your inquiry
        </h2>
        <p className="mt-3 max-w-md text-[15px] leading-relaxed text-navy-600" data-id="element-25" data-mp="jny44ha4tehh">
          A member of our team will contact you to discuss the type of fiduciary or
          administrative support you require.
        </p>
        <button type="button" onClick={() => {
        setValues(initialValues);
        setStatus('idle');
      }} className="mt-7 border border-navy-300 px-6 py-3 text-[14px] font-medium text-navy-800 transition-colors duration-150 ease-out hover:border-navy-600" data-id="element-26" data-mp="3jpn31ataynh5">
          Submit another inquiry
        </button>
      </div>;
  }
  return <form noValidate onSubmit={handleSubmit} className="border border-navy-100 bg-white p-6 sm:p-8" data-id="element-27" data-mp="35wfdrpsp242m">
      <div className="grid gap-6 sm:grid-cols-2" data-id="element-28" data-mp="2h062v7sg6kn">
        <div data-id="element-29" data-mp="2ib4sgmdmo4w">
          <label className={labelClass} htmlFor="fullName" data-id="element-30" data-mp="2fp14s2lwsry">
            Full Name
          </label>
          <input id="fullName" name="fullName" type="text" autoComplete="name" className={fieldClass} value={values.fullName} onChange={event => update('fullName', event.target.value)} aria-invalid={Boolean(errors.fullName)} aria-describedby={errors.fullName ? 'fullName-error' : undefined} data-id="element-31" data-mp="3tpnrtc9xxvsq" />
          {errors.fullName && <p id="fullName-error" className="mt-2 text-[13px] text-red-700" data-id="element-32" data-mp="285sgjjjwhuvx">
              {errors.fullName}
            </p>}
        </div>

        <div data-id="element-33" data-mp="2d1za0cv41dwz">
          <label className={labelClass} htmlFor="email" data-id="element-34" data-mp="2huab3j1l75r4">
            Email Address
          </label>
          <input id="email" name="email" type="email" autoComplete="email" className={fieldClass} value={values.email} onChange={event => update('email', event.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} data-id="element-35" data-mp="2lwe07ah6i8uv" />
          {errors.email && <p id="email-error" className="mt-2 text-[13px] text-red-700" data-id="element-36" data-mp="11h27jl45xhv7">
              {errors.email}
            </p>}
        </div>

        <div data-id="element-37" data-mp="16hhnmkftx3gv">
          <label className={labelClass} htmlFor="phone" data-id="element-38" data-mp="yepfrxhtyujf">
            Phone Number
          </label>
          <input id="phone" name="phone" type="tel" autoComplete="tel" className={fieldClass} value={values.phone} onChange={event => update('phone', event.target.value)} data-id="element-39" data-mp="tfm9k9q46i9v" />
        </div>

        <div data-id="element-40" data-mp="13fvc05ap8zxp">
          <label className={labelClass} htmlFor="state" data-id="element-41" data-mp="1hdjux7l7hx04">
            State
          </label>
          <select id="state" name="state" className={fieldClass} value={values.state} onChange={event => update('state', event.target.value)} aria-invalid={Boolean(errors.state)} aria-describedby={errors.state ? 'state-error' : undefined} data-id="element-42" data-mp="1hb75k9d4ckd1">
            <option value="" data-id="element-43" data-mp="2039q3vsvsksk">Select a state</option>
            {usStates.map(state => <option key={state} value={state} data-id="element-44" data-mp="1w2zgm95vrc8y">
                {state}
              </option>)}
          </select>
          {errors.state && <p id="state-error" className="mt-2 text-[13px] text-red-700" data-id="element-45" data-mp="2eonltxtotq7u">
              {errors.state}
            </p>}
        </div>

        <div data-id="element-46" data-mp="25qv0od1abocj">
          <label className={labelClass} htmlFor="service" data-id="element-47" data-mp="25pa7aqqpibg9">
            Service of Interest
          </label>
          <select id="service" name="service" className={fieldClass} value={values.service} onChange={event => update('service', event.target.value)} aria-invalid={Boolean(errors.service)} aria-describedby={errors.service ? 'service-error' : undefined} data-id="element-48" data-mp="2anlh51h04lyh">
            <option value="" data-id="element-49" data-mp="1r0w6smgriz2v">Select a service</option>
            {services.map(service => <option key={service.slug} value={service.name} data-id="element-50" data-mp="1d1o4hkpazaug">
                {service.name}
              </option>)}
            <option value="General Inquiry" data-id="element-51" data-mp="1hzfug96e0mw0">General Inquiry</option>
          </select>
          {errors.service && <p id="service-error" className="mt-2 text-[13px] text-red-700" data-id="element-52" data-mp="1xhz0wyraiuza">
              {errors.service}
            </p>}
        </div>

        <fieldset data-id="element-53" data-mp="1ohkcvrmxz1s3">
          <legend className={labelClass} data-id="element-54" data-mp="1oh1533r09trs">Preferred Contact Method</legend>
          <div className="flex gap-6 py-3" data-id="element-55" data-mp="1jipu2poz5d7z">
            {['Email', 'Phone'].map(method => <label key={method} className="flex items-center gap-2 text-[15px] text-navy-800" data-id="element-56" data-mp="1ejmql9on7f24">
                <input type="radio" name="contactMethod" value={method} checked={values.contactMethod === method} onChange={event => update('contactMethod', event.target.value)} className="h-4 w-4 accent-navy-900" data-id="element-57" data-mp="19s4p8kipjcgm" />
                {method}
              </label>)}
          </div>
        </fieldset>

        <div className="sm:col-span-2" data-id="element-58" data-mp="qwelarr7s603">
          <label className={labelClass} htmlFor="message" data-id="element-59" data-mp="32i71c71yvz2d">
            Message
          </label>
          <textarea id="message" name="message" rows={5} className={fieldClass} value={values.message} onChange={event => update('message', event.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'message-error' : undefined} data-id="element-60" data-mp="37fpc82zjood0" />
          {errors.message && <p id="message-error" className="mt-2 text-[13px] text-red-700" data-id="element-61" data-mp="2op7j3xasvqr4">
              {errors.message}
            </p>}
        </div>
      </div>

      {submitError && <div role="alert" className="mt-6 flex items-start gap-3 border border-red-200 bg-red-50 p-4" data-id="element-62" data-mp="2xiug1qqqv01w">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-700" aria-hidden="true" data-id="element-63" data-mp="20zmyl9z00lfq" />
          <p className="text-[14px] leading-relaxed text-red-800" data-id="element-64" data-mp="24znfr2dhfaqh">{submitError}</p>
        </div>}

      <button type="submit" disabled={status === 'submitting'} className="mt-8 inline-flex items-center justify-center gap-2 bg-navy-900 px-8 py-3.5 text-[15px] font-medium text-white transition-colors duration-150 ease-out hover:bg-navy-800 disabled:opacity-70" data-id="element-65" data-mp="3fl1mx6hlgr1v">
        {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-66" data-mp="2wt0ilom1w8l2" />}
        {status === 'submitting' ? 'Sending…' : 'Submit Inquiry'}
      </button>

      <p className="mt-6 text-[13px] leading-relaxed text-navy-500" data-id="element-67" data-mp="35svz88ydkvsh">
        Please do not submit confidential financial, medical, legal or other
        sensitive information through this form. A member of our team will contact
        you to discuss your inquiry.
      </p>
    </form>;
}